/**
 * Firebase Cloud Messaging client.
 *
 * Two paths depending on where we're running:
 *
 *   1. **Inside the Capacitor native app** (Android APK / iOS IPA).
 *      We dispatch to the @capacitor/push-notifications plugin via
 *      `window.Capacitor.Plugins.PushNotifications` — this returns a
 *      real FCM/APNs registration token that the OS will use to deliver
 *      *native* notifications (using the app's launcher icon, theme
 *      colour, sound). No service worker involved. We never import the
 *      plugin statically because the web bundle isn't compiled with
 *      Capacitor — the plugin is injected by the native shell at
 *      runtime.
 *
 *   2. **In a regular browser** (Chrome on desktop / mobile web). We
 *      use the Firebase JS SDK + the platform service worker. Push
 *      payloads are delivered via Web Push and render in the browser's
 *      notification UI.
 *
 * Both paths POST the resulting token to /api/push/fcm/register so the
 * backend can route to either channel transparently.
 */
import { api } from "./api";

let _firebaseApp = null;
let _messaging = null;
let _config = null;
let _configFetchPromise = null;

function isCapacitorNative() {
  // `window.Capacitor.isNativePlatform()` is auto-injected by Capacitor
  // at runtime in the native shell. Treat any non-function value as
  // "not native" so the call is safe in browsers + SSR.
  const cap = typeof window !== "undefined" ? window.Capacitor : null;
  return !!(cap && typeof cap.isNativePlatform === "function" && cap.isNativePlatform());
}

function nativePushPlugin() {
  return window?.Capacitor?.Plugins?.PushNotifications || null;
}

async function loadConfig() {
  if (_config) return _config;
  if (_configFetchPromise) return _configFetchPromise;
  _configFetchPromise = (async () => {
    try {
      const { data } = await api.get("/firebase/public-config");
      if (data && data.configured) {
        _config = data;
      } else {
        _config = null;
      }
    } catch {
      _config = null;
    }
    return _config;
  })();
  return _configFetchPromise;
}

export async function isFcmConfigured() {
  // Inside the native app we always have FCM via the plugin —
  // /firebase/public-config returns the VAPID key for web only.
  if (isCapacitorNative()) return true;
  const cfg = await loadConfig();
  return !!cfg;
}

async function ensureInitialized() {
  if (_messaging) return _messaging;
  const cfg = await loadConfig();
  if (!cfg) return null;
  const { initializeApp } = await import("firebase/app");
  const { getMessaging, isSupported } = await import("firebase/messaging");
  if (!(await isSupported())) {
    return null;
  }
  _firebaseApp = initializeApp({
    apiKey: cfg.apiKey,
    authDomain: cfg.authDomain,
    projectId: cfg.projectId,
    storageBucket: cfg.storageBucket,
    messagingSenderId: cfg.messagingSenderId,
    appId: cfg.appId,
  });
  _messaging = getMessaging(_firebaseApp);
  return _messaging;
}

/**
 * Native-app path: ask Capacitor's PushNotifications plugin for a real
 * FCM/APNs token. Returns the token, or null when the plugin says no
 * (permission denied, no Google Play Services, etc).
 */
async function getNativePushToken() {
  const plugin = nativePushPlugin();
  if (!plugin) {
    console.warn("[fcm] Capacitor native but PushNotifications plugin missing — falling back");
    return null;
  }
  try {
    console.log("[fcm] native: requesting push permission…");
    const perm = await plugin.requestPermissions();
    console.log("[fcm] native: permission =", perm);
    if (perm?.receive !== "granted") {
      console.warn("[fcm] native push permission not granted:", perm);
      return null;
    }

    // CRITICAL: wire the listeners BEFORE calling register(). Some
    // Capacitor versions (and the underlying FirebaseMessaging SDK)
    // fire `registration` synchronously on Android when a token is
    // cached — if we call register() before addListener() resolves, we
    // miss the event and time out after 15s.
    let regHandle = null;
    let errHandle = null;
    const token = await new Promise((resolve, reject) => {
      let settled = false;
      const cleanup = () => {
        if (regHandle) { try { regHandle.remove(); } catch { /* noop */ } }
        if (errHandle) { try { errHandle.remove(); } catch { /* noop */ } }
      };
      const onReg = (t) => {
        if (settled) return;
        settled = true;
        cleanup();
        const value = t?.value || null;
        console.log("[fcm] native: registration event, token =",
                    value ? value.slice(0, 24) + "…" : "(empty)");
        resolve(value);
      };
      const onErr = (e) => {
        if (settled) return;
        settled = true;
        cleanup();
        // Surface the FULL event payload — Capacitor's PushNotifications
        // plugin sends `{ error: "<iOS localized description>" }`. If that
        // string is empty (common when iOS rejects registration before
        // even attempting the APNs handshake), fall back to JSON of the
        // entire object so the diagnostic toast can show something useful.
        console.warn("[fcm] native: registrationError event =", JSON.stringify(e));
        const msg = (e?.error && String(e.error).trim())
                 || (e?.message && String(e.message).trim())
                 || (() => {
                      try { return JSON.stringify(e || {}); }
                      catch { return null; }
                    })()
                 || "registrationError (no detail provided by iOS)";
        const err = new Error(msg);
        err.raw = e;
        reject(err);
      };
      // 30-second safety net (some networks need a full 20s for the
      // first FCM token mint).
      setTimeout(() => {
        if (settled) return;
        settled = true;
        cleanup();
        reject(new Error("Native push registration timed out after 30s"));
      }, 30000);

      // Await both addListener() promises BEFORE register() to avoid
      // the synchronous-fire race.
      Promise.all([
        plugin.addListener("registration", onReg),
        plugin.addListener("registrationError", onErr),
      ])
        .then(([h1, h2]) => {
          regHandle = h1;
          errHandle = h2;
          console.log("[fcm] native: listeners armed → calling register()");
          return plugin.register();
        })
        .catch(onErr);
    });

    // Foreground delivery handlers — fired by the OS when the app is
    // open at the moment a push arrives. Android does NOT auto-show
    // system-tray notifications when the app is foregrounded; iOS shows
    // banners by default but the user wants in-app feedback regardless.
    // We render a toast + emit a window event so any page can react
    // (refresh order list, ring a bell, etc).
    _wireForegroundPushListeners(plugin);

    return token;
  } catch (e) {
    console.warn("[fcm] native push registration failed:", e?.message || e);
    // Surface the actual error to the caller — swallowing it hides
    // critical signals like "no valid 'aps-environment'" or "APNs not
    // configured for this Firebase iOS app". A bare `return null` was
    // forcing PushDiagnostics to show a misleading generic message.
    const err = new Error(e?.message || e?.error || "registrationError");
    err.code = e?.code || e?.error || "registrationError";
    err.platform = "native";
    throw err;
  }
}

// One-shot setup so we don't stack listeners across re-registrations.
let _foregroundListenersWired = false;
function _wireForegroundPushListeners(plugin) {
  if (_foregroundListenersWired) return;
  _foregroundListenersWired = true;
  try {
    plugin.addListener("pushNotificationReceived", (n) => {
      try {
        const title = n?.title || n?.data?.title || "Notification";
        const body = n?.body || n?.data?.body || "";
        const url = n?.data?.url || n?.data?.click_action || "/";
        // Lightweight UI surface — `sonner` toast (already in this app).
        import("sonner").then(({ toast }) => {
          toast(title, {
            description: body,
            action: url && url !== "/" ? {
              label: "Open",
              onClick: () => { try { window.location.href = url; } catch { /* sandbox */ } },
            } : undefined,
          });
        }).catch(() => { /* sonner not present, ignore */ });
        // Dispatch a window event so any subscriber (e.g. NotificationBell)
        // can refresh its unread count immediately.
        try {
          window.dispatchEvent(new CustomEvent("kb:push-received", { detail: n }));
        } catch { /* sandbox */ }
      } catch (err) {
        console.warn("[fcm] foreground push handler error:", err);
      }
    });
    plugin.addListener("pushNotificationActionPerformed", (action) => {
      try {
        const url = action?.notification?.data?.url
          || action?.notification?.data?.click_action;
        if (url && typeof url === "string") {
          window.location.href = url;
        }
      } catch (err) {
        console.warn("[fcm] push action handler error:", err);
      }
    });
  } catch (err) {
    console.warn("[fcm] couldn't wire foreground listeners:", err);
  }
}

/**
 * Request a token using the right channel for this environment. Returns
 * the token string, or null if FCM isn't available / permission denied.
 *
 * Throws an error with a `.code` field on native registration failures
 * (so the diagnostic UI can show the actual reason — APNs entitlement
 * missing, APNs key not in Firebase, etc — instead of a generic
 * "permission denied" message).
 *
 * Caller MUST have already obtained Notification permission via the
 * browser API on the web path. On native, this function asks the OS
 * directly via Capacitor.
 */
export async function getFcmToken() {
  let token = null;

  if (isCapacitorNative()) {
    // Let native errors bubble up to the caller with their real message.
    token = await getNativePushToken();
  } else {
    try {
      const messaging = await ensureInitialized();
      if (!messaging) return null;
      const cfg = await loadConfig();
      if (!cfg) return null;
      const { getToken } = await import("firebase/messaging");
      let swReg = null;
      if ("serviceWorker" in navigator) {
        try {
          swReg = await navigator.serviceWorker.ready;
        } catch { /* sw may not be registered yet on first load */ }
      }
      token = await getToken(messaging, {
        vapidKey: cfg.vapidKey,
        serviceWorkerRegistration: swReg || undefined,
      });
    } catch (e) {
      console.warn("[fcm] web getToken failed:", e?.message || e);
      return null;
    }
  }

  if (token) {
    try { localStorage.setItem("kb_fcm_token", token); } catch { /* sandbox */ }
    try {
      await api.post("/push/fcm/register", {
        token,
        user_agent: (navigator.userAgent || "").slice(0, 200),
      });
      console.log("[fcm] backend registered token successfully");
    } catch (err) {
      console.warn("[fcm] backend /push/fcm/register FAILED:",
                   err?.response?.status, err?.response?.data || err?.message);
      // CRITICAL: clear the local cache so the next call retries the
      // backend POST. Otherwise the token sits on the device but
      // backend never knows about it → silent failure forever.
      try { localStorage.removeItem("kb_fcm_token"); } catch { /* sandbox */ }
    }
  } else {
    console.warn("[fcm] no token returned — registration did not produce one");
  }
  return token;
}

export async function unregisterFcmToken() {
  let token = null;
  try { token = localStorage.getItem("kb_fcm_token"); } catch { /* sandbox */ }
  if (!token) return;
  try {
    await api.post("/push/fcm/unregister", { token });
  } catch { /* best effort */ }
  try { localStorage.removeItem("kb_fcm_token"); } catch { /* sandbox */ }
}
